/**
 * AddMembershipModal.tsx
 *
 * Membership enrollment flow matching the edit modal layout:
 * membership selector + preview card at top, payment/billing in the middle,
 * with action buttons in the footer.
 */
import { useEffect, useState, useCallback, useMemo, useId, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle2 } from 'lucide-react';
import {
  createRecurring,
  type BillingMethod,
  type Merchant,
  type PaymentTerminal,
  type RecurringPrepayPayload,
} from '@/services/payments.service';
import {
  listTemplates,
  type MembershipTemplate,
} from '@/services/memberships.service';
import { getErrorCode, getErrorDetails, getErrorMessage } from '@/services/apiClient';
import { parseDateValue, todayYmd } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { useAuth } from '@/hooks/useAuth';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import {
  PaymentSourceSelector,
  parseTerminalToken,
} from '@/components/payments/PaymentSourceSelector';
import {
  PREPAY_BY_CARD,
  PrepayMethodFields,
  buildPrepayPayload,
  isSavedCardToken,
  useRecordedPrepayMethods,
} from '@/components/memberships/PrepayMethodFields';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import {
  TerminalCheckoutPanel,
  type TerminalFlowResult,
} from '@/components/payments/TerminalCheckoutPanel';
import {
  GiftCardSelector,
  type AppliedGiftCard,
} from '@/components/payments/GiftCardSelector';
import { DatePicker } from '@/components/ui/DatePicker';

function fmtCurrency(value: number | null | undefined): string {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

/* ── Prepaid months (plan 2026-10-22-001, R1-R3, R5, R6, R33) ───────── */

/** 0 means no prepayment; one prepayment buys 1 to 12 months (R5). */
const PREPAY_MONTH_OPTIONS = Array.from({ length: 13 }, (_, i) => i);

interface SaveError {
  heading?: string;
  message: string;
  note?: string;
}

/**
 * What staff see when the save fails. A prepaid sign-up takes the payment
 * before it saves anything (KTD7), so its two failures say what became of the
 * money: a declined card saved nothing but the declined payment (R33), and a
 * save that failed after the payment refunded or voided it.
 */
function saveErrorFor(err: unknown): SaveError {
  const code = getErrorCode(err);
  if (code === 'PAYMENT_DECLINED') {
    return {
      heading: 'The card was declined, so the membership was not saved.',
      message: getErrorMessage(err, 'Transaction error: Payment was declined.'),
      note: "The declined payment stays on the patient's account.",
    };
  }
  if (code === 'SAVE_FAILED') {
    const details = getErrorDetails(err);
    const undone = details?.refunded === true
      ? 'The prepayment charge was refunded'
      : details?.voided === true
        ? 'The prepayment was voided'
        : null;
    return {
      heading: 'The membership could not be saved.',
      message: undone
        ? `${undone}, and nothing was saved.`
        : getErrorMessage(err, 'The membership could not be saved after the prepayment was taken.'),
    };
  }
  return { message: getErrorMessage(err, 'Failed to create membership. Please try again.') };
}

/* ── Template Preview Card (matches edit modal MembershipTitleCard) ── */

function TemplatePreviewCard({
  template,
  merchantTitle,
  amount,
  onAmountChange,
  amountEditable,
  startDate,
  onStartDateChange,
  minDate,
}: {
  template: MembershipTemplate;
  merchantTitle: string;
  amount: string;
  onAmountChange: (value: string) => void;
  amountEditable: boolean;
  startDate: string;
  onStartDateChange: (value: string) => void;
  minDate?: string;
}) {
  const products = template.products ?? [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-slate-900">{template.title}</div>
          {merchantTitle && (
            <div className="text-xs text-slate-500">{merchantTitle}</div>
          )}

          <p className="mt-3 mb-2 text-xs font-medium text-slate-400">
            Products included per cycle
          </p>
          {products.length > 0 ? (
            <div className="space-y-1">
              {products.map((p) => (
                <div key={p.templateProductId} className="flex items-baseline gap-3 text-base">
                  <span className="w-8 text-right font-semibold text-slate-900 tabular-nums">{p.quantity}</span>
                  <span className="text-slate-800">{p.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No products included</p>
          )}
        </div>

        <div className="flex-shrink-0 text-right">
          <p className="text-xs font-medium text-slate-500">Amount / Month</p>
          {amountEditable ? (
            <input
              type="text"
              inputMode="decimal"
              value={`$${amount}`}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9.]/g, '');
                const parts = raw.split('.');
                const cleaned = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : raw;
                onAmountChange(cleaned);
              }}
              onBlur={() => {
                const num = parseFloat(amount);
                if (!Number.isNaN(num)) onAmountChange(num.toFixed(2));
              }}
              className="mt-1 w-32 border-0 border-b-2 border-transparent bg-transparent text-right text-2xl font-bold text-slate-900 focus:border-blue-500 focus:outline-none"
            />
          ) : (
            <p className="mt-1 text-2xl font-bold text-slate-900">
              {fmtCurrency(parseFloat(amount) || 0)}
            </p>
          )}
          <div className="mt-3">
            <div className="text-xs font-medium text-slate-500">Start Date</div>
            <DatePicker
              size="sm"
              value={startDate}
              min={minDate}
              onChange={onStartDateChange}
              ariaLabel="Start date"
              wrapperClassName="mt-1 w-40"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Modal ────────────────────────────────────────────────────── */

interface AddMembershipModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function AddMembershipModal({
  patientId,
  open,
  onClose,
  onSaved,
}: AddMembershipModalProps) {
  const { hasMinUserLevel } = useAuth();
  const canEditPrice = hasMinUserLevel(2);

  const [saveOverlay, setSaveOverlay] = useState<'saving' | 'saved' | null>(null);
  // saveOverlay is only set once the mutation runs, so it can't stop a quick
  // second click: this is set synchronously and blocks a second charge.
  const savingRef = useRef(false);
  const [error, setError] = useState<SaveError | null>(null);

  const queryClient = useQueryClient();

  const [merchantId, setMerchantId] = useState<number | null>(null);
  const [merchantTitle, setMerchantTitle] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [paymentToken, setPaymentToken] = useState('new');
  const [selectedMethod, setSelectedMethod] = useState<BillingMethod | null>(null);
  const [selectedTerminal, setSelectedTerminal] = useState<PaymentTerminal | null>(null);
  const [, setTerminalResult] = useState<TerminalFlowResult | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [startDate, setStartDate] = useState(todayYmd);
  const [memo, setMemo] = useState('');
  const [giftCards, setGiftCards] = useState<AppliedGiftCard[]>([]);

  // Prepaid months. `null` follows the R2 default for the start date until
  // staff pick a value themselves; the card choice follows the membership's
  // own card until staff pick another.
  const [prepayMonthsChoice, setPrepayMonthsChoice] = useState<number | null>(null);
  const [prepayMethodChoice, setPrepayMethodChoice] = useState(PREPAY_BY_CARD);
  const [prepayCardChoice, setPrepayCardChoice] = useState<{ token: string; method: BillingMethod | null } | null>(null);
  const [prepayReference, setPrepayReference] = useState('');
  const prepayMonthsId = useId();
  const prepayTotalLabelId = useId();

  const { data: templates } = useQuery({
    queryKey: ['membership-templates', 'recurring'],
    queryFn: () => listTemplates({ incrementType: 1, limit: 200 }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const activeTemplates = useMemo(
    () => (Array.isArray(templates) ? templates : []).filter(
      (t) => t.active && !/-{4,}/.test(t.title),
    ),
    [templates],
  );

  // The methods a prepayment can be recorded with (R6).
  const recordedMethods = useRecordedPrepayMethods(open);

  useEffect(() => {
    if (!open) return;
    setSaveOverlay(null);
    setError(null);
    setMerchantId(null);
    setMerchantTitle('');
    setFacilityId('');
    setPaymentToken('new');
    setSelectedMethod(null);
    setSelectedTerminal(null);
    setTerminalResult(null);
    setSaveWarning(null);
    setSelectedTemplateId(null);
    setAmount('');
    setStartDate(todayYmd());
    setMemo('');
    setGiftCards([]);
    setPrepayMonthsChoice(null);
    setPrepayMethodChoice(PREPAY_BY_CARD);
    setPrepayCardChoice(null);
    setPrepayReference('');
  }, [open]);

  useEffect(() => {
    if (selectedTemplateId == null || !activeTemplates.length) return;
    const template = activeTemplates.find((t) => t.templateId === selectedTemplateId);
    if (template) {
      setAmount(template.price.toFixed(2));
    }
  }, [selectedTemplateId, activeTemplates]);

  const parsedAmount = useMemo(() => {
    const n = Number(amount);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [amount]);
  // Local calendar date, not UTC — the modal can be open across the UTC
  // rollover (after 5pm Pacific), and a UTC "today" would lock `min` to
  // tomorrow and make an immediate enrollment impossible.
  const today = todayYmd();
  const willChargeNow = startDate === today;

  const selectedTemplate = useMemo(
    () => activeTemplates.find((t) => t.templateId === selectedTemplateId) ?? null,
    [activeTemplates, selectedTemplateId],
  );

  // R2: 0 whatever the start date. A start today is charged its first month
  // by Save & Charge; prepaying it as well would only add a prepayment and a
  // used credit to the payment history.
  const prepayMonths = prepayMonthsChoice ?? 0;
  const prepayByCard = prepayMethodChoice === PREPAY_BY_CARD;
  // One new-card form at a time: the prepayment offers "New card" only once
  // the membership's own new-card form is closed, and a prepayment "New card"
  // picked earlier gives way when that form opens again.
  const prepayNewCardAllowed = paymentToken !== 'new';
  const prepayCardPick = prepayCardChoice?.token === 'new' && !prepayNewCardAllowed ? null : prepayCardChoice;
  // The prepayment card defaults to the membership's own card once one is saved.
  const prepayCardToken = prepayCardPick?.token ?? (isSavedCardToken(paymentToken) ? paymentToken : '');
  const prepayCardMethod = prepayCardPick ? prepayCardPick.method : selectedMethod;
  // R1, R5: the monthly amount times the months, for the membership chosen.
  const prepayTotal = selectedTemplate ? Math.round(parsedAmount * prepayMonths * 100) / 100 : 0;
  // A $0 membership has nothing to prepay; the API refuses one.
  const takesPrepayment = selectedTemplate !== null && prepayMonths > 0 && parsedAmount > 0;
  // A card is charged now by a card prepayment, or, with none, by today's
  // first charge. A recorded prepayment starting today pays its first month
  // with a credit, so no card is charged.
  const chargesCardNow = takesPrepayment ? prepayByCard : willChargeNow;

  // True while the user has a terminal selected as the payment source AND
  // has not yet completed tokenize. Once tokenize succeeds, paymentToken is
  // flipped to the returned saved-card token, parseTerminalToken returns
  // null, and isTerminalMode goes false — the form becomes submittable
  // through the standard saveMutation path with the new token.
  const isTerminalMode = parseTerminalToken(paymentToken) !== null;

  const handleMerchantChange = useCallback((id: number, merchant: Merchant) => {
    setMerchantId(id);
    setMerchantTitle(merchant.friendlyTitle ?? '');
    setError(null);
    if (merchant.facilityId) setFacilityId(String(merchant.facilityId));
  }, []);

  const handleSourceChange = useCallback(
    (token: string, method: BillingMethod | null, terminal: PaymentTerminal | null = null) => {
      setPaymentToken(token);
      setSelectedMethod(method);
      setSelectedTerminal(terminal);
      setTerminalResult(null);
      setSaveWarning(null);
    },
    [],
  );

  const handleNewSourceSaved = useCallback((method: BillingMethod) => {
    setSelectedMethod(method);
    setPaymentToken(method.token);
    setSelectedTerminal(null);
    setTerminalResult(null);
    setSaveWarning(null);
    setError(null);
  }, []);

  // Tokenize completed on the terminal. Lift save_warning into the modal
  // (it survives the panel unmount that happens when paymentToken flips
  // off the terminal handle), then promote the returned saved-card token
  // to paymentToken so the existing saveMutation flow picks it up — same
  // outcome as legacy "Connect" Memberships flow which captures a reusable
  // token then submits the recurring row with it.
  const handleTerminalResult = useCallback(
    (result: TerminalFlowResult) => {
      setTerminalResult(result);
      setSaveWarning(result.data.save_warning ?? null);
      if (result.mode === 'tokenize') {
        const token = result.data.token;
        if (token) {
          setPaymentToken(token);
          // Refresh the saved-card list so the new card appears in the
          // dropdown (and so selectedMethod can be repopulated by the
          // user if they want to reselect from the source list).
          void queryClient.invalidateQueries({ queryKey: ['patient-billing-methods', patientId] });
        }
      }
    },
    [queryClient, patientId],
  );

  const handleTerminalReset = useCallback(() => {
    setTerminalResult(null);
    setSaveWarning(null);
  }, []);

  const handlePrepayCardChange = useCallback((token: string, method: BillingMethod | null) => {
    setPrepayCardChoice({ token, method });
  }, []);

  const saveMutation = useMutation({
    mutationFn: async () => {
      setSaveOverlay('saving');
      setError(null);

      if (selectedTemplateId == null) throw new Error('Select a membership template.');
      if (!merchantId) throw new Error('Select a merchant account.');
      if (parsedAmount < 0 || amount === '') throw new Error('Enter a valid amount.');
      if (paymentToken === 'new' && parsedAmount > 0) {
        throw new Error('Complete new-card entry or select a saved payment source.');
      }

      const prepay: RecurringPrepayPayload | null = takesPrepayment
        ? buildPrepayPayload(
            prepayMonths,
            prepayMethodChoice,
            {
              token: prepayCardToken,
              title: prepayCardMethod?.title,
              paymentMethodID: prepayCardMethod?.paymentMethodID,
            },
            recordedMethods,
            prepayReference,
          )
        : null;

      // parseDateValue reads a bare 'YYYY-MM-DD' as LOCAL midnight; a bare
      // `new Date(startDate)` would land on UTC midnight and `.getDate()`
      // would read back as the previous day west of Greenwich.
      const derivedChargeDay =
        parseDateValue(startDate)?.getDate() ?? new Date().getDate();

      const result = await createRecurring({
        patientID: patientId,
        facilityID: facilityId ? Number(facilityId) : undefined,
        paymentMethodID: selectedMethod?.paymentMethodID ?? undefined,
        paymentMethodToken: paymentToken,
        incrementType: '1',
        chargeDay: derivedChargeDay,
        startDate,
        curAmount: parsedAmount,
        strPayType: 'Recurring',
        strPayMethod: selectedMethod?.title ?? 'Forward',
        strMemo: memo.trim() || undefined,
        accountMerchantId: String(merchantId),
        membershipTemplateIds: [selectedTemplateId],
        giftCardIds: giftCards
          .map((card) => card.giftCard.giftCardId ?? card.giftCard.paymentsCreditID)
          .filter((id): id is number => typeof id === 'number'),
        chargeNow: willChargeNow,
        ...(prepay ? { prepay } : {}),
      });

      return result;
    },
    onSuccess: async () => {
      onSaved();
      setSaveOverlay('saved');
      await new Promise((r) => setTimeout(r, 800));
      setSaveOverlay(null);
      onClose();
    },
    onError: (err: unknown) => {
      console.error('[AddMembershipModal] Failed:', err);
      setSaveOverlay(null);
      // The form keeps everything staff entered, so they can fix it and save again.
      setError(saveErrorFor(err));
      const code = getErrorCode(err);
      if (code === 'PAYMENT_DECLINED' || code === 'SAVE_FAILED') {
        // The prepayment's payment row exists either way (declined, refunded or voided).
        void queryClient.invalidateQueries({ queryKey: ['patient-payments', patientId] });
      }
    },
  });

  if (!open) return null;

  // Submit is blocked when (a) no real card token is selected yet, or (b) a
  // terminal is selected but tokenize hasn't yet promoted paymentToken to a
  // real saved-card token. The isTerminalMode gate prevents the saveMutation
  // from shipping a literal "terminal|123" handle to the backend.
  const isZeroAmount = parsedAmount === 0;
  const canSubmit = (paymentToken !== 'new' || isZeroAmount) && !isTerminalMode && selectedTemplateId != null;

  return (
    <Modal
      isOpen={open}
      onClose={saveOverlay ? () => {} : onClose}
      title="Add Membership"
      size="xl"
      footer={
        <Button
          onClick={() => {
            if (savingRef.current) return;
            savingRef.current = true;
            saveMutation.mutate(undefined, { onSettled: () => { savingRef.current = false; } });
          }}
          disabled={!canSubmit || saveOverlay !== null}
        >
          {chargesCardNow ? 'Save & Charge' : 'Save'}
        </Button>
      }
      overlay={
        saveOverlay ? (
          <div className="flex flex-col items-center gap-3">
            {saveOverlay === 'saving' ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-slate-600">Saving…</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <span className="text-sm font-medium text-green-700">Saved</span>
              </>
            )}
          </div>
        ) : undefined
      }
    >
      {error && (
        <div role="alert" className="mb-4 space-y-1 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.heading ? <p className="font-semibold">{error.heading}</p> : null}
          <p>{error.message}</p>
          {error.note ? <p className="text-xs">{error.note}</p> : null}
        </div>
      )}

      <div className="space-y-5">
          {/* ── SECTION 1: Membership Selector + Preview ── */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-slate-700">Membership</div>
            <SearchableCombobox<number>
              value={selectedTemplateId}
              onChange={(val) => setSelectedTemplateId(val)}
              clearable
              options={activeTemplates.map<ComboboxOption<number>>((t) => ({
                value: t.templateId,
                label: t.title,
                meta: fmtCurrency(t.price) + ' / mo',
              }))}
              placeholder="Search membership templates..."
              emptyMessage={activeTemplates.length === 0 ? 'No templates available.' : 'No matches'}
            />
            {selectedTemplate && (
              <TemplatePreviewCard
                template={selectedTemplate}
                merchantTitle={merchantTitle}
                amount={amount}
                onAmountChange={setAmount}
                amountEditable={canEditPrice}
                startDate={startDate}
                onStartDateChange={setStartDate}
                minDate={today}
              />
            )}
          </div>

          {/* ── SECTION 2: Payment & Billing ── */}
          <div className="rounded-lg border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
              <h3 className="text-sm font-semibold text-slate-700">Payment & Billing</h3>
            </div>
            <div className="space-y-4 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <MerchantSelector value={merchantId} onChange={handleMerchantChange} />
                <PaymentSourceSelector
                  patientId={patientId}
                  value={paymentToken}
                  merchantId={merchantId}
                  onChange={handleSourceChange}
                />
              </div>
              <ForwardBillingMethodCapture
                active={paymentToken === 'new'}
                patientId={patientId}
                merchantId={merchantId}
                onSaved={handleNewSourceSaved}
              />
              {isTerminalMode && selectedTerminal ? (
                <TerminalCheckoutPanel
                  terminal={selectedTerminal}
                  patientId={patientId}
                  // Tokenize on memberships matches legacy
                  // payment-details-recurring-authnet2.php "Connect" flow:
                  // capture a reusable saved-card token only, never charge
                  // on the device. The recurring billing engine charges
                  // that token on the membership's start date / cycle.
                  amount={0}
                  merchantId={merchantId}
                  mode="tokenize"
                  onSuccess={handleTerminalResult}
                  onReset={handleTerminalReset}
                />
              ) : null}
              {saveWarning ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <p className="font-semibold">Payment method not saved.</p>
                  <p className="mt-1 text-xs">{saveWarning}</p>
                </div>
              ) : null}

              {/* ── Prepaid months: label column / field column, so Total
                   Upfront lines up with Months to prepay above it ── */}
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] items-center gap-x-4 gap-y-2 sm:grid-cols-[10rem_12rem]">
                  <label htmlFor={prepayMonthsId} className="text-sm font-medium text-slate-700">
                    Months to prepay
                  </label>
                  <select
                    id={prepayMonthsId}
                    value={prepayMonths}
                    onChange={(e) => setPrepayMonthsChoice(Number(e.target.value))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {PREPAY_MONTH_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  {prepayMonths > 0 ? (
                    <>
                      <span id={prepayTotalLabelId} className="text-sm font-medium text-slate-700">
                        Total Upfront
                      </span>
                      <output
                        aria-labelledby={prepayTotalLabelId}
                        className="px-3 text-base font-semibold text-slate-900 tabular-nums"
                      >
                        {fmtCurrency(prepayTotal)}
                      </output>
                    </>
                  ) : (
                    <p className="col-span-2 text-sm text-slate-500">
                      {willChargeNow
                        ? "The first month is charged to the membership's card when you save."
                        : 'The membership will start billing automatically on its start date.'}
                    </p>
                  )}
                </div>

                {prepayMonths > 0 ? (
                  <PrepayMethodFields
                    patientId={patientId}
                    merchantId={merchantId}
                    recordedMethods={recordedMethods}
                    choice={prepayMethodChoice}
                    onChoiceChange={setPrepayMethodChoice}
                    cardToken={prepayCardToken}
                    onCardChange={handlePrepayCardChange}
                    allowNewCard={prepayNewCardAllowed}
                    referenceNumber={prepayReference}
                    onReferenceNumberChange={setPrepayReference}
                  />
                ) : null}
              </div>

              <label className="space-y-1 text-sm text-slate-700">
                <span className="font-medium">Notes</span>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>

              <div className="border-t border-slate-200 pt-4">
                <GiftCardSelector
                  appliedCards={giftCards}
                  onChange={setGiftCards}
                  maxAmount={parsedAmount}
                  patientId={patientId}
                />
              </div>
            </div>
          </div>
        </div>
    </Modal>
  );
}
